import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { Chip } from '@/components/ui/chip'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ValidatorRowEntry } from './types'

interface ValidatorChecksTableProps {
  rows: ValidatorRowEntry[]
  /** Developer persona: show the raw rule id under the humanized label (F15). */
  showRuleIds?: boolean
}

export function ValidatorChecksTable({ rows, showRuleIds }: ValidatorChecksTableProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.validatorPanel
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-border bg-muted px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {copy.allChecks}
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          {copy.noViolations}
        </div>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead className="sr-only">
            <tr>
              <th>{copy.colGate}</th>
              <th>{copy.colRule}</th>
              <th>{copy.statusPass}</th>
              <th>{copy.statusFail}</th>
              <th>{copy.colStatus}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.gate}-${row.ruleId}`} className={cn(i > 0 && 'border-t border-border')}>
                <td className="px-4 py-2.5">
                  <Chip>{row.gate}</Chip>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {row.ruleLabel}
                  {showRuleIds && (
                    <div
                      className="mt-0.5 font-mono text-[10px] text-muted-foreground/70"
                      data-testid={`row-rule-id-${row.ruleId}`}
                    >
                      {row.ruleId}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-success">
                  <span className="inline-flex items-center gap-1">
                    {row.passCount}
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                </td>
                <td
                  className={cn(
                    'px-4 py-2.5 text-right',
                    row.failCount > 0 ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    {row.failCount}
                    <X className="h-3 w-3" aria-hidden="true" />
                  </span>
                </td>
                <td className="w-24 px-4 py-2.5">
                  <Badge
                    variant={row.status}
                    data-testid={`row-status-${row.ruleId}`}
                    data-variant={row.status}
                  >
                    {row.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}
